import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Navigate } from "@tanstack/react-router"
import { useState } from "react"
import { useAuth } from "../hooks/useAuth"
import { extractErrorMessage } from "../lib/api/errors"
import { fetchOAuthClientInfo, submitOAuthConsent } from "../lib/api/mcp"
import { queryKeys } from "../lib/query-keys"
import { useProduct, useTenant } from "../lib/tenant"
import { ConsentView } from "./-oauth-consent/ConsentView"
import type { TConsentState } from "./-oauth-consent/ConsentView/interfaces"

interface IConsentSearch {
  client_id?: string
  scope?: string
  consent_code?: string
}

/**
 * OAuth consent step for Connect Claude. Better Auth sends a signed-in user
 * here (with the consent code in a signed cookie and on the query string) before
 * any client receives an authorization code. Allow / Deny answers the consent
 * endpoint, which hands back the client's redirect URI to finish the flow.
 */
function ConsentPage(): React.JSX.Element | null {
  const { user, loading: authLoading } = useAuth()
  const { client_id: clientId, consent_code: consentCode } = Route.useSearch()
  const branding = useTenant().branding
  const product = useProduct()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const clientQuery = useQuery({
    queryKey: queryKeys.oauthClient(clientId ?? ""),
    queryFn: () => fetchOAuthClientInfo(clientId ?? ""),
    enabled: Boolean(user) && Boolean(clientId),
    retry: false,
  })

  if (authLoading) return null
  if (!user) {
    const here = `${window.location.pathname}${window.location.search}`
    return <Navigate to="/login" search={{ redirect: here }} replace />
  }

  const state: TConsentState = (() => {
    if (!clientId) return "invalid"
    if (clientQuery.isLoading) return "loading"
    if (clientQuery.isError || clientQuery.data?.disabled) return "invalid"
    return submitting ? "submitting" : "ready"
  })()

  const answer = async (accept: boolean): Promise<void> => {
    setError(undefined)
    setSubmitting(true)
    try {
      const redirectUri = await submitOAuthConsent(accept, consentCode)
      window.location.replace(redirectUri)
    } catch (err) {
      setError(extractErrorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <ConsentView
      logo={branding?.logo}
      appName={branding?.name}
      product={product}
      loginBackground={branding?.loginBackground}
      clientName={clientQuery.data?.name}
      redirectHosts={clientQuery.data?.redirectHosts}
      userEmail={user.email}
      state={state}
      error={error}
      onAllow={() => void answer(true)}
      onDeny={() => void answer(false)}
    />
  )
}

export const Route = createFileRoute("/oauth/consent")({
  component: ConsentPage,
  validateSearch: (search: Record<string, unknown>): IConsentSearch => ({
    client_id: typeof search.client_id === "string" ? search.client_id : undefined,
    scope: typeof search.scope === "string" ? search.scope : undefined,
    consent_code: typeof search.consent_code === "string" ? search.consent_code : undefined,
  }),
})
